/**
 * График платежей кредитов и лизинга: «По графику», «Оплачено», «Осталось» по строке и
 * итогом, бейджи статуса и проверка суммы отметки оплаты — по тем же правилам, что сервер
 * (`osnova-storage/src/ceo/debts.rs`: `schedule_unpaid`, `mark_debt_schedule_paid`).
 * Итоги считают остаток — как календарь по месяцам и KPI. Модуль без импортов значений —
 * проверяется node:test (`scheduleModel.test.mjs`).
 */
import type { CeoDebtScheduleItem } from '@shared/api';

/** Остаток меньше копейки — платёж оплачен (как на сервере). */
export const PAID_EPS = 0.01;

type ScheduleMoney = Pick<CeoDebtScheduleItem, 'total_payment' | 'paid_amount' | 'status'>;

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export interface ScheduleAmounts {
  /** По графику. */
  scheduled: number;
  paid: number;
  /** Осталось оплатить. */
  left: number;
}

/** По графику, оплачено и осталось по строке; у оплаченной строки осталось ноль. */
export function rowAmounts(item: ScheduleMoney): ScheduleAmounts {
  const scheduled = round2(item.total_payment);
  const paid = round2(item.paid_amount);
  const left = item.status === 'paid' ? 0 : round2(Math.max(scheduled - paid, 0));
  return { scheduled, paid, left };
}

/** Итог строк: по графику, оплачено, осталось. */
export function scheduleTotals(items: readonly ScheduleMoney[]): ScheduleAmounts {
  return items.reduce<ScheduleAmounts>(
    (acc, item) => {
      const a = rowAmounts(item);
      return {
        scheduled: round2(acc.scheduled + a.scheduled),
        paid: round2(acc.paid + a.paid),
        left: round2(acc.left + a.left),
      };
    },
    { scheduled: 0, paid: 0, left: 0 },
  );
}

/** Платёж закрыт: осталось не больше копейки. */
export function isRowPaid(item: ScheduleMoney): boolean {
  return rowAmounts(item).left <= PAID_EPS;
}

/** У строки есть отметка оплаты (полной или частичной) — её можно снять. */
export function hasPaymentMark(item: Pick<CeoDebtScheduleItem, 'paid_amount' | 'status'>): boolean {
  return item.status === 'paid' || item.paid_amount > 0.005;
}

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

export interface RowBadge {
  label: string;
  tone: BadgeTone;
}

/**
 * Статус строки бейджами: «Оплачено»; «Частично оплачено» (и «Просрочено», если срок
 * прошёл); «Просрочено»; «Плановый».
 */
export function rowBadges(
  item: Pick<CeoDebtScheduleItem, 'total_payment' | 'paid_amount' | 'status' | 'days_until_due'>,
): RowBadge[] {
  if (isRowPaid(item)) return [{ label: 'Оплачено', tone: 'success' }];
  const badges: RowBadge[] = [];
  if (item.status === 'overdue' || item.days_until_due < 0) {
    badges.push({ label: 'Просрочено', tone: 'danger' });
  }
  if (item.paid_amount > 0.005) badges.push({ label: 'Частично оплачено', tone: 'warning' });
  if (badges.length === 0) badges.push({ label: 'Плановый', tone: 'neutral' });
  return badges;
}

/**
 * Основание оплаты под статусом: документ («п/п № …», «По таблице Excel») или
 * «основание не указано», если документа у отметки нет — ни у ручной, ни у старой
 * отметки загрузкой таблицы. Строка без оплаты — `null`.
 */
export function paymentNote(
  item: Pick<CeoDebtScheduleItem, 'payment_reference' | 'paid_amount' | 'status'>,
): string | null {
  if (!hasPaymentMark(item)) return null;
  const reference = item.payment_reference?.trim();
  return reference ? reference : 'основание не указано';
}

/** Что не так с суммой отметки оплаты: пусто или не число, ноль, больше остатка. */
export type AmountProblem = 'empty' | 'zero' | 'over';

/** Сумма отметки оплаты — разобранная `parseMoney`; сервер так же откажет в переплате. */
export function markAmountProblem(amount: number | null, left: number): AmountProblem | null {
  if (amount === null) return 'empty';
  if (amount <= 0) return 'zero';
  if (amount > left + PAID_EPS) return 'over';
  return null;
}
